import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Coins } from "lucide-react";

export default function MarketView({ ch, setCh }) {
  const { t } = useTranslation();
  const [listings, setListings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [listItem, setListItem] = useState("");
  const [price, setPrice] = useState(50);

  const refresh = async () => setListings(await api.market());
  useEffect(() => { refresh(); }, []);

  const sellables = (ch.inventory || []).filter(i => i.slot !== "material" || true);

  const listNow = async () => {
    if (!listItem || price <= 0) return;
    setBusy(true);
    try {
      await api.marketList(listItem, price);
      const c = await api.character();
      setCh(c);
      setListItem("");
      await refresh();
      toast.success(t("market.listed"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  const buy = async (lid) => {
    setBusy(true);
    try {
      const res = await api.marketBuy(lid);
      setCh(res.character);
      await refresh();
      toast.success(t("market.boughtFor", { gold: res.paid }));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  const cancel = async (lid) => {
    setBusy(true);
    try {
      await api.marketCancel(lid);
      const c = await api.character();
      setCh(c);
      await refresh();
      toast(t("market.withdrawn"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  // §MARKET-BG: viewport-wide Ravencross backdrop applied via a CSS
  // class on <body> (see index.css `.market-bg-active`). We also drop
  // game-root's inline gradient so the body's image shows through the
  // 1024px column. Every value restored on unmount.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.add("market-bg-active");
    const root = document.querySelector('[data-testid="game-root"]');
    const prev = root ? { bg: root.style.background, bc: root.style.backgroundColor } : null;
    if (root) {
      root.style.background = "transparent";
      root.style.backgroundColor = "transparent";
    }
    return () => {
      document.body.classList.remove("market-bg-active");
      if (root && prev) {
        root.style.background = prev.bg;
        root.style.backgroundColor = prev.bc;
      }
    };
  }, []);

  return (
    <div className="space-y-2" data-testid="market-view">
      <div className="panel p-3 space-y-2" data-testid="market-list-form">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">{t("market.listItem")}</div>
        <select
          data-testid="market-item-select"
          value={listItem}
          onChange={(e) => setListItem(e.target.value)}
          className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-2 text-sm text-[#E2DFD8]"
        >
          <option value="">{t("market.choose")}</option>
          {sellables.map(i => (
            <option key={i.id} value={i.id}>{i.name} ({i.rarity})</option>
          ))}
        </select>
        <div className="flex gap-2">
          <input
            data-testid="market-price-input"
            type="number"
            min={1}
            value={price}
            onChange={(e) => setPrice(parseInt(e.target.value) || 0)}
            className="flex-1 bg-[#0a0a0a] border border-[#262626] px-2 py-2 text-sm text-[#E2DFD8]"
          />
          <button
            data-testid="market-list-btn"
            onClick={listNow}
            disabled={busy || !listItem}
            className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase disabled:opacity-30"
          >{t("market.list")}</button>
        </div>
        <p className="text-[10px] text-[#666] font-mono">{t("market.sellerFee")}</p>
      </div>

      <div className="panel p-2" data-testid="market-listings">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] px-2 py-1">
          {t("market.title")} ({listings.length})
        </div>
        {listings.length === 0 && (
          <p className="italic text-[#666] text-xs px-2 py-3">{t("market.empty")}</p>
        )}
        {listings.map(l => {
          const isMine = l.seller_id === ch.user_id;
          return (
            <div key={l.listing_id} data-testid={`listing-${l.listing_id}`}
              className={`panel-inner p-2 mb-1 flex justify-between items-center border-l-4 rarity-${l.item.rarity}`}>
              <div className="min-w-0">
                <div className={`font-heading text-sm truncate rarity-${l.item.rarity}`}>{l.item.name}</div>
                <div className="font-mono text-[10px] text-[#8c8c8c] uppercase tracking-wider">
                  {l.item.slot} · {l.item.rarity} · {t("market.by")} {l.seller_name}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-sm text-[#E5C34A] flex items-center gap-1">
                  <Coins className="w-3 h-3" strokeWidth={1.5} /> {l.price}
                </span>
                {isMine ? (
                  <button onClick={() => cancel(l.listing_id)}
                    className="px-2 py-1 text-[10px] font-heading tracking-widest uppercase border border-[#333] text-[#8c8c8c]"
                  >{t("market.cancel")}</button>
                ) : (
                  <button data-testid={`buy-${l.listing_id}`}
                    onClick={() => buy(l.listing_id)}
                    disabled={busy || ch.gold < l.price}
                    className="px-2 py-1 text-[10px] font-heading tracking-widest uppercase border border-[#4A5D23] text-[#6E8B3D] disabled:opacity-30"
                  >{t("market.buy")}</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
